import { DndContext } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bars4Icon } from '@heroicons/react/24/outline';
import { forwardRef, Fragment, FunctionComponent, ReactNode, useCallback, useImperativeHandle, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Form } from 'softcat:components/form.tsx';

type FormSortableListProperties = {
    label: ReactNode;
    name: string;
    renderField: (fieldProperties: { field: unknown; index: number; name: string; remove: (index: number) => void }) => ReactNode;
};

type SortableItemProperties = {
    children: (sortableProperties: Omit<ReturnType<typeof useSortable>, 'setNodeRef' | 'transform' | 'transition'>) => ReactNode;
    id: string;
};

const SortableItem: FunctionComponent<SortableItemProperties> = ({ children, id }) => {
    const { setNodeRef, transform, transition, ...rest } = useSortable({ id });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
            }}
        >
            {children({ ...rest })}
        </div>
    );
};

export const FormSortableList = forwardRef<{ prepend: (item: unknown) => void }, FormSortableListProperties>(({ label, name, renderField }, reference) => {
    const { control } = useFormContext();
    const { fields, prepend, remove, move } = useFieldArray({ control, name });
    const sortableContainer = useRef<HTMLDivElement | null>(null);

    const handlePrepend = useCallback(
        (item: unknown) => {
            prepend(item);

            /* v8 ignore start */
            setTimeout(() => {
                if (sortableContainer.current) {
                    sortableContainer.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 500);
            /* v8 ignore end */
        },
        [prepend],
    );

    useImperativeHandle(reference, () => {
        return { prepend: handlePrepend };
    }, [handlePrepend]);

    return (
        <div className="sm:grid sm:grid-cols-4 sm:items-start sm:gap-4">
            <Form.Label htmlFor={name}>{label}</Form.Label>

            <div className={'mt-2 sm:col-span-3 sm:mt-0'}>
                <div className={'h-72 overflow-y-scroll rounded-lg shadow-sm ring-1 ring-gray-200'}>
                    <DndContext
                        /* v8 ignore start */
                        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                        onDragEnd={(event) => {
                            const { active, over } = event;

                            if (over && active.id !== over?.id) {
                                const activeIndex = active.data.current?.sortable?.index;
                                const overIndex = over.data.current?.sortable?.index;

                                if (activeIndex !== undefined && overIndex !== undefined) {
                                    move(activeIndex, overIndex);
                                }
                            }
                        }}
                        /* v8 ignore end */
                    >
                        <SortableContext items={fields}>
                            <div className={'flex flex-col gap-3 p-2'} ref={sortableContainer}>
                                {fields.map((field, index) => {
                                    return (
                                        <Fragment key={field.id}>
                                            <SortableItem id={field.id}>
                                                {({ attributes, listeners }) => {
                                                    return (
                                                        <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-4 py-1">
                                                            <Bars4Icon
                                                                className="size-5 shrink-0 cursor-move select-none text-gray-200 outline-0"
                                                                data-testid={`sortable-list-item-${index}-handle`}
                                                                {...attributes}
                                                                {...listeners}
                                                            />
                                                            {renderField({ field, index, name, remove })}
                                                        </div>
                                                    );
                                                }}
                                            </SortableItem>
                                        </Fragment>
                                    );
                                })}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            </div>
        </div>
    );
});

FormSortableList.displayName = 'FormSortableList';
